import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { AccessPolicy } from '@medplum/fhirtypes';

// -----------------------------------------------------------------------------
interface UseAccessPoliciesReturn {
  accessPolicies: AccessPolicy[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// -----------------------------------------------------------------------------
export const useAccessPolicies = (
  shouldLoad: boolean = true
): UseAccessPoliciesReturn => {
  const medplum = useMedplum();
  const { t } = useTranslation(['admin', 'common']);

  const [accessPolicies, setAccessPolicies] = useState<AccessPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  const loadAccessPolicies = useCallback(async () => {
    if (!shouldLoad) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const policies = await medplum.searchResources('AccessPolicy', {
        _count: 100,
        _sort: 'name',
      });

      setAccessPolicies(policies);
    } catch (err) {
      console.error('Error loading access policies:', err);
      const errorMessage = 'Failed to load access policies';
      setError(errorMessage);
      notifications.show({
        color: 'red',
        title: t('error', { ns: 'common' }),
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [medplum, shouldLoad, t]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadAccessPolicies();
  }, [loadAccessPolicies]);

  // ---------------------------------------------------------------------------
  return {
    accessPolicies,
    loading,
    error,
    refetch: loadAccessPolicies,
  };
};
